import DOMPurify from 'dompurify';
import { getStatusColor } from 'helpers/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';

const PreviewTicket = ({ data, hoveredElement = null }) => {
  const cardRef = useRef(null);
  const [cardPosition, setCardPosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  const sanitizeHtml = useMemo(
    () => DOMPurify.sanitize(data.description),
    [data]
  );

  useEffect(() => {
    if (hoveredElement) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 200); // Pequeno delay para evitar flickering

      return () => {
        clearTimeout(timer);
        setIsVisible(false);
      };
    } else {
      setIsVisible(false);
    }
  }, [hoveredElement]);

  const calculatePosition = hoveredRect => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const cardWidth = 500;
    const cardHeight = Math.min(600, viewportHeight * 0.8);
    const safeGap = 15;
    const padding = 20; // Padding das bordas da tela

    // POSIÇÃO HORIZONTAL
    let newX = hoveredRect.right + safeGap;

    // Se não couber à direita, posicionar à esquerda
    if (newX + cardWidth > viewportWidth - padding) {
      newX = hoveredRect.left - cardWidth - safeGap;
    }

    // Se ainda não couber à esquerda, forçar a posição mais próxima possível
    if (newX < padding) {
      newX = padding;
    }

    // POSIÇÃO VERTICAL - Sempre alinhar com o topo do elemento hoverado
    let newY = hoveredRect.top;

    // Verificar se o preview vai sair da parte inferior da tela
    const bottomOverflow = newY + cardHeight - (viewportHeight - padding);

    if (bottomOverflow > 0) {
      // Se vai sair por baixo, subir o necessário para ficar dentro da tela
      newY = newY - bottomOverflow;

      // Garantir que não suba demais e saia por cima
      if (newY < padding) {
        newY = padding;
      }
    }

    // Verificar se o preview vai sair da parte superior da tela
    if (newY < padding) {
      newY = padding;
    }

    return { x: newX, y: newY };
  };

  useEffect(() => {
    if (cardRef.current && hoveredElement && isVisible) {
      const hoveredRect = hoveredElement.getBoundingClientRect();
      const position = calculatePosition(hoveredRect);
      setCardPosition(position);
    }
  }, [hoveredElement, isVisible]);

  // Atualizar posição em caso de scroll ou resize
  useEffect(() => {
    if (!isVisible || !hoveredElement) return;

    const updatePosition = () => {
      if (hoveredElement) {
        const hoveredRect = hoveredElement.getBoundingClientRect();
        const position = calculatePosition(hoveredRect);
        setCardPosition(position);
      }
    };

    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isVisible, hoveredElement]);

  if (!isVisible || !hoveredElement) {
    return null;
  }

  return (
    <Card
      ref={cardRef}
      className="position-fixed shadow-lg"
      style={{
        width: '500px',
        maxWidth: '90vw',
        minWidth: '300px',
        zIndex: 99999,
        left: cardPosition.x,
        top: cardPosition.y,
        maxHeight: '80vh',
        overflow: 'hidden',
        pointerEvents: 'none',
        borderRadius: '8px',
        transition: 'opacity 0.2s ease-in-out'
      }}
    >
      <Card.Header
        style={{
          maxHeight: '80px',
          overflow: 'hidden',
          padding: '8px 12px',
          position: 'relative'
        }}
      >
        {/* Indicador visual de alinhamento */}
        <div
          style={{
            position: 'absolute',
            left: '-3px',
            top: '0',
            bottom: '0',
            width: '3px',
            backgroundColor: getStatusColor(data?.status.status || data.status),
            borderRadius: '0 2px 2px 0'
          }}
        />

        <Card.Title
          style={{
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            fontSize: '0.95rem',
            margin: 0,
            lineHeight: '1.3',
            fontWeight: '600',
            paddingLeft: '8px'
          }}
        >
          {data.subject}
        </Card.Title>
      </Card.Header>

      <Card.Body
        style={{
          maxHeight: 'calc(80vh - 80px)',
          overflow: 'auto',
          padding: '12px'
        }}
      >
        <div className="d-flex mb-3">
          <div
            style={{
              width: 8,
              height: 40,
              backgroundColor: getStatusColor(
                data?.status.status || data.status
              ),
              flexShrink: 0,
              borderRadius: '4px'
            }}
            className="me-2"
          />
          <div className="d-flex flex-column" style={{ minWidth: 0 }}>
            <p
              className="m-0 mb-1"
              style={{ fontSize: '0.9rem', fontWeight: '500' }}
            >
              {data.status?.description}
            </p>
            <p className="m-0 text-muted" style={{ fontSize: '0.85rem' }}>
              #{data.code}
            </p>
          </div>
        </div>

        <div>
          <div
            className="rich-text-content"
            style={{
              width: '100%',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontSize: '0.9rem',
              lineHeight: '1.5'
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml }}
          />
        </div>
      </Card.Body>
    </Card>
  );
};

export default PreviewTicket;
